import { useMemo } from 'react';

import { useResponsive } from '@sg/garnish';
import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe-features-customization';

import { buildCustomizationIngredientRows } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens the modification groups into the virtualized list's rows, sorting
 * each section's ingredients the way the eager grid used to.
 */
export const useCustomizationIngredientRows = (params: Params) => {
  const { actor, modificationGroups } = params;

  const { match } = useResponsive();
  const { sortModifications } = useCustomizationHelpers({ actor });
  const { initialModificationsMap } = useCustomizationContext({ actor });

  // ─── Derived ─────────────────────────────────────────────────────────

  const itemsPerRow = match(INGREDIENTS_PER_ROW);

  const rows = useMemo(() => {
    if (!modificationGroups || !initialModificationsMap) return [];

    const sections = modificationGroups.map((group) => ({
      group,
      modifications: sortModifications(initialModificationsMap, group),
    }));

    return buildCustomizationIngredientRows({ sections, itemsPerRow });
  }, [modificationGroups, initialModificationsMap, sortModifications, itemsPerRow]);

  // ─────────────────────────────────────────────────────────────────────

  return { rows, itemsPerRow };
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Ingredients per row, per breakpoint. Mirrors the stack the eager grid used.
 */
const INGREDIENTS_PER_ROW = [3, 2, 4, 5];

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: CustomizationMachineActorRef;
  modificationGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
};
